import { useEffect } from 'react';
import Layout from '../../components/layout/Layout';
import Breadcrumb from '../../components/layout/Breadcrumb';
import { yenInc, jbaDispatchExTax } from '../../utils/pricing';

// ===== 執事サービス ハブ 全面最新化（Implementer A スコープ） =====
// デザイン土台: src/pages/home/page.tsx + src/pages/about/overview/page.tsx の新スタイル
//   （紺#1a2942 / 真鍮金#a38e65 / 金差し色見出し / BTN-08 2状態 / 細罫エディトリアル / 紺引用パネル / タイムライン）。
// カタログ対応: 採用表 docs/design-catalog/index.html（Hero=BB-02 / 見出し=H2-02系 / 区切り=DIV-01,02 /
//   セクション型=SEC-N02/N04/N07/N08 の翻案。新バリエーションは各節に由来注記）。
// 事実正典（創作禁止・出典path記録）:
//   [S-hub] mng-thebutler/00_事業説明.md L69-81（プラン全体像・料金の考え方）
//   [S-price] 現行 working tree 実文: services/footman・butler・second-steward・house-steward/page.tsx（各料金表）
//   [S-concept] mng-thebutler/00_事業説明.md L19-27（理念・提供価値）/ L44-55（提供対象・内容）
// 実データ（料金・時間・関係性）は上記正典のまま。変更禁止。

export default function ButlerServicesPage() {
  useEffect(() => {}, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '執事サービス' }
  ];

  // BTN-08（overview / home と同一文法・2状態）
  const BTN08_ON_LIGHT = "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[38px] py-[15px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[38px] py-[15px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  // [S-hub][S-price] 4プラン（フットマン→バトラー→セカンドスチュワード→ハウススチュワードの序列）
  const plans = [
    {
      order: '01', tier: 'Ⅰ', name: 'Footman', ja: 'フットマン',
      href: '/services/footman/', img: '/images/gen2/jba-service-footman-guest-reception-01.jpg', aspect: 'aspect-video', fit: 'object-cover',
      alt: '晩餐会で銀盆の飲み物を来客へ届けるフットマンのサービス場面',
      cadence: '3時間からのスポット利用', price: yenInc(jbaDispatchExTax.footmanBase), unit: '円〜（税込・3時間）',
      lead: 'イベントやお試し、外出同行など、はじめての方が最も気軽に執事を体験できる入口のプランです。パーティーの給仕から来客対応、日常の一時的なサポートまで、必要な時間だけプロの執事にお任せいただけます。',
      scenes: ['ホームパーティー・来客対応', 'ビジネスイベントの受付・給仕', '記念日や一時的な家事の集中サポート'],
    },
    {
      order: '02', tier: 'Ⅱ', name: 'Butler', ja: 'バトラー',
      href: '/services/butler/', img: '/images/gakken/gk-14.jpg', aspect: 'aspect-video',
      alt: '銀盆にティーセットを載せて一日支援の給仕準備を整える専属バトラー',
      cadence: '1日8時間の専属サービス', price: yenInc(jbaDispatchExTax.butlerBase), unit: '円〜（税込・8時間）',
      lead: 'フットマンの一歩先。1日8時間というまとまった時間を確保することで、朝の準備から夕の片付けまで、執事本来の「先を読んだ気配り」を一日を通してご体験いただけます。富裕層・VIPのお客様に相応しい本格的なおもてなしです。',
      scenes: ['重要な商談・会議のある一日', '大切なゲストの終日接待', '療養時や多忙な一日のトータルサポート'],
    },
    {
      order: '03', tier: 'Ⅲ', name: 'Second Steward', ja: 'セカンドスチュワード',
      href: '/services/second-steward/', img: '/images/gen2/jba-service-second-steward-schedule-01.jpg', aspect: 'aspect-video', fit: 'object-cover',
      alt: '月間予定表を依頼者と確認し、継続的な邸宅管理を調整するセカンドスチュワード',
      cadence: '1ヶ月20日間の継続利用', price: yenInc(jbaDispatchExTax.secondStewardMonthly), unit: '円〜（税込・月額）',
      lead: 'バトラーの単発利用を継続へ。1人の専属執事が1ヶ月20日間通うことで、お客様の生活リズムや好みを深く理解し、言葉にする前に整う「真のパーソナライズド」を実現します。富裕層・ファミリーオフィス向けの定期サービスです。',
      scenes: ['多忙な経営者・エグゼクティブの生活基盤', '産前産後・高齢のご家族の継続ケア', '別荘・プライベートオフィスの定期管理'],
    },
    {
      order: '04', tier: 'Ⅳ', name: 'House Steward', ja: 'ハウススチュワード',
      href: '/services/house-steward/', img: '/images/gen2/jba-service-house-steward-team-01.jpg', aspect: 'aspect-[3/4]', fit: 'object-cover',
      alt: '邸宅図面と鍵を確認し、三名体制で常駐管理を引き継ぐハウススチュワードチーム',
      cadence: '24時間・3人交代制の常駐型', price: yenInc(jbaDispatchExTax.houseStewardMonthly), unit: '円（税込・月額）',
      lead: '協会が誇る最高峰。3人の熟練執事が8時間交代で常時待機し、24時間365日、途切れることのない最上級のサービスを提供します。VVIP・超富裕層・ファミリーオフィスのお客様に、邸宅全体を統括する究極のパートナーシップをお届けします。',
      scenes: ['超多忙な経営者・著名人の生活統括', 'ファミリーオフィス・大規模邸宅の管理', '24時間の見守りが必要なご家庭'],
    },
  ];

  return (
    <Layout>
      <title>執事サービス｜4つのプランと料金 | 全日本執事協会 THE BUTLER</title>
      <meta name="description" content="全日本執事協会が運営する執事人材サービス「THE BUTLER」。3時間のフットマンから24時間常駐のハウススチュワードまで、暮らしとビジネスを支える4つのプランと料金をご案内します。" />
      <link rel="canonical" href="https://butler-association.jp/butler-services" />

      {/* ===== ヒーロー — BB-02 左寄せビルボード（画像+紺グラデ暗幕）。由来: overview ヒーロー ===== */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <img
          src="/images/generated/jba-p-013-bs-thebutler-w2.webp"
          alt="THE BUTLER 執事サービス。品格ある執事が暮らしとビジネスを静かに支える"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[720px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">THE BUTLER — Services</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(28px,3.4vw,44px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">暮らしとビジネスを、</span>
              <span className="block">執事とともに整える</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              全日本執事協会が運営する執事人材サービス「THE BUTLER」。英国式の規律と品格、日本ならではの細やかな礼節を兼ね備えたプロフェッショナルが、短時間のスポット利用から24時間の常駐まで、お客様のニーズに合わせて暮らしとビジネスを包括的に支えます。単なる家事の代行ではなく、本来向き合うべき「仕事」「家族」「休息」に集中できる環境そのものをお届けします。
            </p>
          </div>
        </div>
      </section>
      {/* パンくず: ビルボード直後に配置（Yuichi 2026-07-17 指摘②）。共通Breadcrumbのヘッダー逃げpaddingを子セレクタで通常paddingへ上書き */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-14 sm:py-18 lg:py-20 space-y-16 sm:space-y-20">

          {/* --- S1. THE BUTLER が選ばれる理由（金eyebrow+金差し色見出し+リード+細罫データ行）。由来: overview S1 --- */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">I. Why THE BUTLER</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              人手の代替ではなく、<span className="text-[#a38e65]">時間と心の余裕</span>を
            </h2>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
              <p>
                THE BUTLER の執事は、130年以上の系譜を持つ全日本執事協会が育てたプロフェッショナルです。厳格な教育プログラムと約3年の育成期間を経て、技能だけでなく品格・教養・「仕える心」を備えた人材だけが、お客様の暮らしの内側に入ります。
              </p>
              <p>
                主人が言葉にする前にニーズを察知し、空気のように自然に動く「先回りの奉仕」を本質とします。段取りや雑事から解放されることで、お客様は本当に大切なことへ時間と意識を注げるようになります。
              </p>
              <p>
                協会・執事・お客様の間で結ぶ双方向の守秘義務契約と、双方への反社会的勢力チェックを徹底。証券会社の厳格な審査にも耐えうるコンプライアンス水準で、安心してプライベートな領域までお任せいただけます。
              </p>
            </div>
            {/* 細罫データ行（22-26px級serifで重要データを強調） */}
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4 mt-10 border-t border-[#e2ddd2] pt-8">
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">歴史</dt>
                <dd className="font-['Playfair_Display',serif] text-[26px] text-[#1a2942]">130<span className="text-sm text-[#8f7a56] ml-1">年超</span></dd>
              </div>
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">育成期間</dt>
                <dd className="font-['Playfair_Display',serif] text-[26px] text-[#1a2942]">約3<span className="text-sm text-[#8f7a56] ml-1">年</span></dd>
              </div>
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">プラン</dt>
                <dd className="font-['Playfair_Display',serif] text-[26px] text-[#1a2942]">4<span className="text-sm text-[#8f7a56] ml-1">種</span></dd>
              </div>
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">運営</dt>
                <dd className="font-['Noto_Serif_JP',serif] text-lg text-[#1a2942] leading-tight pt-1">全日本執事協会</dd>
              </div>
            </dl>
          </section>

          {/* --- S2. 4つのプラン（序列ステップ型・画像splitエディトリアル行）= 新バリエーション①
               由来注記: overview S4 番号付き2カラムブロック + home split を、
               「フットマン→ハウススチュワード」の序列を左の連番ステップで可視化する縦積みへ拡張。
               各行の関係性（次段への段階）を明示。トップに序列ステップ型は無いため新造。 --- */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">II. The Four Plans</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              暮らしに合わせて選ぶ、<span className="text-[#a38e65]">4つのプラン</span>
            </h2>
            <div className="space-y-5 max-w-3xl mb-10 font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
              <p>
                THE BUTLER のサービスは、利用時間と関わりの深さで4つのプランに分かれます。フットマン、バトラー、セカンドスチュワード、ハウススチュワードという名称は、英国の伝統的な使用人職制——家令（スチュワード）を最上位に、執事（バトラー）がフットマンを監督し、フットマン（従僕）が来客の対応や食卓の給仕を担う——に敬意を込めて名付けた、THE BUTLER独自のサービスプラン名です。歴史上の職制の序列や等級そのものではなく、いずれのプランも協会の認定執事が担当します。
              </p>
              <p>
                まずは短時間のフットマンプランから、必要に応じてバトラー、セカンドスチュワード、ハウススチュワードの各プランへ。関わりが深まるほど、執事はお客様を深く理解し、サービスの精度を高めていきます。
              </p>
            </div>

            <div className="space-y-10">
              {plans.map((p, i) => (
                <div key={p.name} className="relative grid lg:grid-cols-[300px_1fr] gap-6 lg:gap-10 border-t border-[#e2ddd2] pt-9">
                  {/* 連番ステップ列 */}
                  <div className="flex lg:flex-col gap-5">
                    <img src={p.img} alt={p.alt} className={`w-32 h-28 lg:w-full lg:h-56 ${(p as { fit?: string }).fit ?? 'object-cover'} border border-[#a38e65]/30`} />
                    <div className="flex items-start gap-3">
                      <span className="font-['Playfair_Display',serif] text-[#d7b779] text-4xl leading-none">{p.order}</span>
                      <div className="pt-1">
                        <span className="block font-['Playfair_Display',serif] text-[13px] tracking-[0.14em] text-[#8f7a56]">STEP {p.tier}</span>
                        <span className="jba-display text-[#1a2942] text-lg">{p.ja}</span>
                      </div>
                    </div>
                  </div>
                  {/* 内容列 */}
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-3">
                      <h3 className="jba-display font-medium text-[#1a2942] text-xl sm:text-2xl">{p.name}</h3>
                      <span className="font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56]">{p.cadence}</span>
                    </div>
                    <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9] max-w-2xl mb-6">{p.lead}</p>
                    <div className="grid sm:grid-cols-[1fr_auto] gap-6 items-end">
                      <ul className="space-y-2.5">
                        {p.scenes.map((s) => (
                          <li key={s} className="flex items-start gap-3 font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.8]">
                            <span className="mt-2 w-1.5 h-1.5 bg-[#a38e65] shrink-0"></span>{s}
                          </li>
                        ))}
                      </ul>
                      <div className="sm:text-right">
                        {/* 料金＝22-26px級serif強調 */}
                        <div className="font-['Noto_Serif_JP',serif] text-[26px] text-[#1a2942] leading-none">
                          {p.price}<span className="text-base text-[#8f7a56] ml-1">{p.unit}</span>
                        </div>
                        <a href={p.href} className={`${BTN08_ON_LIGHT} mt-4`}>{p.name}の詳細を見る</a>
                      </div>
                    </div>
                  </div>
                  {i < plans.length - 1 && (
                    <div className="hidden lg:block absolute left-[150px] -bottom-5 text-[#a38e65]/60 text-lg" aria-hidden="true">↓</div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* --- S3. どのプランを選ぶか（紺引用パネル+選び方の指針）= 新バリエーション②
               由来注記: overview S3 引用大組みパネルを、読者の「どれを選べばいい？」に先回りで答える
               安心先行型の選び方ガイドへ翻案。紺全面パネルに指針の一文＋3つの目安。 --- */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">III. How to Choose</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              どのプランが合うか、迷ったら
            </h2>
            <div className="bg-[#1a2942] px-7 sm:px-12 lg:px-16 py-12 sm:py-14">
              <blockquote className="jba-display font-normal text-[#f8f6f0] text-xl sm:text-2xl lg:text-[28px] leading-[1.65] tracking-[0.02em] max-w-3xl">
                「どれくらいの時間、どれくらい深く関わってほしいか」で選べます。まずはお試しから、必要に応じて段階を上げていくのが、多くのお客様の道のりです。
              </blockquote>
              <div className="w-11 h-px bg-[#a38e65] mt-8 mb-10"></div>
              <div className="grid sm:grid-cols-3 gap-8 lg:gap-10">
                <div>
                  <div className="font-['Playfair_Display',serif] italic text-[#a38e65] text-sm tracking-[0.06em] mb-2">Spot</div>
                  <h3 className="jba-display font-medium text-[#f8f6f0] text-lg mb-2.5">初めて・単発なら</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] leading-[1.9]">まずは<span className="text-[#d7b779]">フットマン</span>。3時間から気軽に執事を体験できます。特別な一日には<span className="text-[#d7b779]">バトラー</span>を。</p>
                </div>
                <div>
                  <div className="font-['Playfair_Display',serif] italic text-[#a38e65] text-sm tracking-[0.06em] mb-2">Monthly</div>
                  <h3 className="jba-display font-medium text-[#f8f6f0] text-lg mb-2.5">継続して支えてほしいなら</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] leading-[1.9]"><span className="text-[#d7b779]">セカンドスチュワード</span>。同じ執事が月20日通い、暮らしの基盤を継続して整えます。</p>
                </div>
                <div>
                  <div className="font-['Playfair_Display',serif] italic text-[#a38e65] text-sm tracking-[0.06em] mb-2">Resident</div>
                  <h3 className="jba-display font-medium text-[#f8f6f0] text-lg mb-2.5">邸宅全体を任せたいなら</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] leading-[1.9]"><span className="text-[#d7b779]">ハウススチュワード</span>。3人体制の24時間常駐で、生活のすべてを統括します。</p>
                </div>
              </div>
            </div>
          </section>

          {/* --- S4. ご利用の流れ（縦タイムライン細罫型）。由来: overview S5 タイムライン --- */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">IV. How It Works</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              ご利用までの5つのステップ
            </h2>
            <ol className="relative border-l border-[#e2ddd2] ml-1 max-w-3xl">
              {[
                { n: '01', t: 'お問い合わせ', d: 'お電話（03-6824-4086）またはお問い合わせフォームから、ご希望の日時・場所・サービス内容をお知らせください。' },
                { n: '02', t: 'ヒアリング・プラン提案', d: '専門スタッフが詳しくご要望を伺い、4つのプランから最適なものと無料のお見積りをご提案します。' },
                { n: '03', t: '執事の選定・ご紹介', d: 'ご要望に最適な執事を選定し、プロフィールをご紹介します。執事のご指名も無料で承ります。' },
                { n: '04', t: 'ご契約・お支払い', d: 'サービス内容と料金をご確認のうえご契約。お支払いは銀行振込またはクレジットカードが可能です。' },
                { n: '05', t: 'サービス開始', d: 'ご指定の日時に執事がお伺いし、プロフェッショナルなサービスを提供します。' },
              ].map((s) => (
                <li key={s.n} className="relative pl-7 sm:pl-9 pb-8 last:pb-0">
                  <span className="absolute -left-[6px] top-1.5 w-[11px] h-[11px] rounded-full bg-[#a38e65] ring-4 ring-white"></span>
                  <div className="flex items-baseline gap-3 mb-1.5">
                    <span className="font-['Playfair_Display',serif] text-lg text-[#8f7a56]">{s.n}</span>
                    <span className="jba-display font-medium text-[#1a2942] text-lg">{s.t}</span>
                  </div>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{s.d}</p>
                </li>
              ))}
            </ol>
          </section>

          {/* --- S5. ご確認ください（細罫リスト型）。由来: overview S2 定義リストの罫線文法を注記リストへ --- */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">V. Good to Know</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              ご利用の前に
            </h2>
            <dl className="border-t-2 border-[#1a2942] max-w-3xl">
              {[
                ['運営', '全日本執事協会が主体となる業務委託・ご紹介ベースのサービスです（労働者派遣ではありません）。'],
                ['提供エリア', '主に東京都内および近郊が中心です。その他の地域はご相談ください。'],
                ['交通費', '各プランの料金に加え、交通費は実費精算（一部プランは月額定額）となります。'],
                ['お申し込み', 'ご利用には事前のお申し込みと確認が必要です。守秘義務契約によりプライバシーを厳守します。'],
              ].map(([k, v]) => (
                <div key={k} className="flex flex-col sm:flex-row gap-1 sm:gap-6 border-b border-[#e2ddd2] py-4">
                  <dt className="sm:w-[124px] sm:shrink-0 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.06em] pt-0.5">{k}</dt>
                  <dd className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#1a2942] leading-[1.85]">{v}</dd>
                </div>
              ))}
            </dl>
          </section>

          {/* --- S6. 締めCTA（紺パネル型）。由来: overview S6 --- */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">VI. Get Started</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              最適な執事サービスをご提案します
            </h2>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                「どのプランが合うか分からない」という段階でも構いません。専門スタッフがご相談を承り、暮らしとご予算に合わせて最適なプランをご提案します。まずはお気軽にお問い合わせください。
              </p>
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                <a href="/contact" className={BTN08_ON_NAVY}>お問い合わせ・ご相談</a>
                <a href="https://thebutler.jp/" target="_blank" rel="noopener noreferrer" className={BTN08_ON_NAVY}>THE BUTLER 公式サイト</a>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
